/**
 * Un réglage de l'APPAREIL, gardé sur lui : le magasin de module qui porte l'apparence de l'espace parent
 * et de l'espace élève (`parentAppearancePreference`, `studentAppearancePreference`), et la largeur de
 * l'activité au live (`liveActivityWidthPreference`).
 *
 * Un magasin de module plutôt qu'une tranche Redux : ce n'est pas une donnée du compte — il survit à un
 * changement de compte et ne part jamais au backend —, et ses lecteurs ne se voient pas.
 * `useSyncExternalStore` les tient d'accord sans provider.
 *
 * La valeur se lit une fois dans le stockage, au premier lecteur, puis s'y écrit à chaque choix. Ce qui
 * se lit passe par `parse` : une valeur inconnue ou une lecture en échec vaut le défaut. Un choix fait
 * pendant la lecture gagne sur la valeur lue : c'est le plus récent des deux.
 */

export type DevicePreferenceStorage = {
    read: (key: string) => Promise<string | null | undefined>
    write: (key: string, value: string) => Promise<unknown>
}

export type DevicePreferenceStore<T extends string> = {
    get: () => T
    set: (next: T) => void
    subscribe: (listener: () => void) => () => void
    /** Lit la valeur gardée. Une seule lecture par magasin, quel que soit le nombre d'appels. */
    hydrate: () => Promise<void>
    /** La lecture a abouti (ou échoué) : `get` dit ce que l'appareil garde. */
    hydrated: () => boolean
}

export type DevicePreferenceOptions<T extends string> = {
    /** La clé où le choix est gardé : c'est elle qu'on retrouve en inspectant le stockage de l'appareil. */
    storageKey: string
    /** Ce que vaut le réglage tant que rien n'est gardé. */
    fallback: T
    /** Ce que le stockage rend, ramené à une valeur connue (le défaut sinon). */
    parse: (raw: unknown) => T
}

/** Le magasin, séparé de son stockage pour se tester sans module simulé. */
export function createDevicePreferenceStore<T extends string>(
    storage: DevicePreferenceStorage,
    options: DevicePreferenceOptions<T>
): DevicePreferenceStore<T> {
    const { storageKey, fallback, parse } = options
    let current: T = fallback
    let hydration: Promise<void> | null = null
    let read = false
    let chosen = false
    const listeners = new Set<() => void>()

    const publish = (next: T) => {
        if (next === current) return
        current = next
        listeners.forEach((listener) => listener())
    }

    return {
        get: () => current,
        subscribe: (listener) => {
            listeners.add(listener)
            return () => {
                listeners.delete(listener)
            }
        },
        hydrate: () => {
            if (hydration == null) {
                hydration = Promise.resolve()
                    .then(() => storage.read(storageKey))
                    .then(
                        (raw) => {
                            if (!chosen) publish(parse(raw))
                        },
                        () => undefined
                    )
                    .then(() => {
                        read = true
                    })
            }
            return hydration
        },
        hydrated: () => read,
        set: (next) => {
            chosen = true
            publish(next)
            // Écrit même quand la valeur ne bouge pas : un choix fait avant la fin de la lecture
            // doit remplacer ce que le stockage garde encore.
            Promise.resolve()
                .then(() => storage.write(storageKey, next))
                .catch(() => undefined)
        },
    }
}
